<template>
  <Dialog
    v-model:visible="displayModal"
    :header="t('templateBuilder.modals.sendTest.header')"
    class="send-test-email-v4-modal p-dialog-maximized"
    :modal="true"
    :dismissable-mask="true"
    @update:visible="handleClose"
  >
    <div class="send-test-v4-layout">
      <!-- Left Panel: Test send configuration -->
      <div class="send-test-v4-left">
        <!-- Send bar card -->
        <div class="send-card">
          <div
            v-if="isRcsChannel"
            class="send-card__sms send-card__sms--rcs"
          >
            <RcsTestRecipientPanel
              :sender-options="rcsSenderOptions"
              :selected-sender-id="rcsSelectedSenderId"
              :selected-agent="rcsSelectedAgent"
              :available-test-numbers="rcsAvailableTestNumbers"
              :selected-test-numbers="rcsSelectedTestNumbers"
              :session-extra-numbers="rcsSessionExtraNumbers"
              :is-shared-sender="rcsIsSharedSender"
              :can-persist="rcsCanPersistTestNumbers"
              :loading="rcsIsLoading"
              @update:selected-sender-id="onRcsSenderChange"
              @update:selected-test-numbers="onRcsTestNumbersChange"
              @add-test-number="onRcsAddTestNumber"
              @persist-test-numbers="onRcsPersistTestNumbers"
            />
            <div class="send-card__sms-row send-card__sms-row--recipient">
              <div class="send-card__recipient-heading">
                <p class="rcs-test-recipient__section-title send-card__rcs-recipient-title">
                  {{ t('templateBuilder.modals.sendTestV4.rcs.recipientSection') }}
                </p>
                <p class="send-card__recipient-hint">
                  {{ t('templateBuilder.modals.sendTestV4.testRecipientHintPhone') }}
                </p>
              </div>
              <div class="send-card__sms-input-group">
                <div class="send-card__field send-card__field--country">
                  <CountryDropdown
                    v-model="countryAndState"
                    :configuration="{ showStates: false, showLabels: false, showFlags: true, showPhonesCodes: true, showOnlyPhoneCodeForSelection: true }"
                    :error="{ country: false, state: false }"
                  />
                </div>
                <div class="send-card__field send-card__field--phone">
                  <AutoComplete
                    ref="autocompleteRef"
                    v-model="v$.fieldValue.$model"
                    class="send-card__autocomplete"
                    input-class="send-card__autocomplete-input"
                    :multiple="false"
                    :suggestions="dataSource"
                    field="value"
                    :placeholder="placeholderKey"
                    :disabled="rcsHasSelectedTestNumbers"
                    @complete="search($event)"
                  />
                </div>
              </div>
            </div>
            <div class="send-card__sms-footer">
              <FieldErrors
                :errors="v$"
                field="fieldValue"
                class="send-card__errors send-card__errors--sms"
              />
              <p
                v-if="rcsRecipientError"
                class="send-card__languages-error"
              >
                {{ rcsRecipientError }}
              </p>
              <Button
                :label="t('templateBuilder.modals.sendTestV4.sendButton')"
                :disabled="!canSendRcsTest"
                icon="fa-light fa-paper-plane"
                class="p-button-success send-card__btn send-card__btn--sms"
                @click="handleValidation"
              />
            </div>
          </div>
          <div
            v-else-if="isPhoneChannel && !isRcsChannel"
            class="send-card__sms"
          >
            <div class="send-card__sms-row">
              <div class="send-card__icon send-card__icon--sms">
                <i :class="channelIconClass" />
              </div>
              <div class="send-card__sms-input-group">
                <div class="send-card__field send-card__field--country">
                  <CountryDropdown
                    v-model="countryAndState"
                    :configuration="{ showStates: false, showLabels: false, showFlags: true, showPhonesCodes: true, showOnlyPhoneCodeForSelection: true }"
                    :error="{ country: false, state: false }"
                  />
                </div>
                <div class="send-card__field send-card__field--phone">
                  <AutoComplete
                    ref="autocompleteRef"
                    v-model="v$.fieldValue.$model"
                    class="send-card__autocomplete"
                    input-class="send-card__autocomplete-input"
                    :multiple="false"
                    :suggestions="dataSource"
                    field="value"
                    :placeholder="placeholderKey"
                    @complete="search($event)"
                  />
                </div>
              </div>
            </div>
            <div class="send-card__sms-footer">
              <FieldErrors
                :errors="v$"
                field="fieldValue"
                class="send-card__errors send-card__errors--sms"
              />
              <Button
                :label="t('templateBuilder.modals.sendTestV4.sendButton')"
                :disabled="v$.$silentErrors.length > 0"
                icon="fa-light fa-paper-plane"
                class="p-button-success send-card__btn send-card__btn--sms"
                @click="handleValidation"
              />
            </div>
          </div>
          <div
            v-else
            class="send-card__inner"
          >
            <div class="send-card__icon">
              <i :class="channelIconClass" />
            </div>
            <div class="send-card__input">
              <AutoComplete
                ref="autocompleteRef"
                v-model="v$.fieldValue.$model"
                class="send-card__autocomplete"
                input-class="send-card__autocomplete-input"
                :multiple="isEmailChannel"
                :suggestions="dataSource"
                field="value"
                :placeholder="placeholderKey"
                @complete="search($event)"
              />
              <FieldErrors
                :errors="v$"
                field="fieldValue"
                class="send-card__errors"
              />
            </div>
            <Button
              :label="t('templateBuilder.modals.sendTestV4.sendButton')"
              :disabled="v$.$silentErrors.length > 0"
              icon="fa-light fa-paper-plane"
              class="p-button-success send-card__btn"
              @click="handleValidation"
            />
          </div>
          <div
            v-if="availableShopLangs.length"
            class="send-card__languages"
          >
            <div class="send-card__languages-header">
              <span class="send-card__languages-title">
                {{ t('templateBuilder.modals.sendTestV4.sendLanguages') }}
              </span>
            </div>
            <div class="send-card__languages-list">
              <button
                v-for="language in availableShopLangs"
                :key="language.id"
                type="button"
                class="send-card__language-item"
                :class="{ 'send-card__language-item--active': selectedLangs.includes(language.id) }"
                @click="toggleLanguage(language.id)"
              >
                <img
                  class="send-card__language-flag"
                  :src="`/images/flags/${language.id}.png`"
                  :alt="language.id"
                >
                <span class="send-card__language-code">{{ language.id.toUpperCase() }}</span>
                <i
                  v-if="selectedLangs.includes(language.id)"
                  class="fa-light fa-check send-card__language-check"
                />
              </button>
            </div>
            <p
              v-if="languageSelectionError"
              class="send-card__languages-error"
            >
              {{ t('templateBuilder.modals.sendTestV4.selectAtLeastOneLanguage') }}
            </p>
          </div>
        </div>

        <div
          v-if="segmentConditions.length > 0"
          class="conditions-card"
        >
          <div class="conditions-card__header">
            <span class="conditions-card__title">{{ t('templateBuilder.modals.sendTestV4.conditionSimulationTitle') }}</span>
          </div>
          <p class="conditions-card__hint">
            {{ t('templateBuilder.modals.sendTestV4.conditionSimulationHint') }}
          </p>
          <div class="conditions-card__list">
            <div
              v-for="(condition, index) in segmentConditions"
              :key="condition.key"
              class="conditions-card__item"
            >
              <span class="conditions-card__badge">C{{ index + 1 }}</span>
              <span class="conditions-card__label">
                {{ t('templateBuilder.modals.sendTestV4.conditionLabel', { index: index + 1 }) }}
              </span>
              <i
                class="fa-light fa-circle-info conditions-card__info"
                @mouseenter.stop="showConditionPopover($event, condition)"
                @mouseleave="hideConditionPopover"
              />
              <InputSwitch
                :model-value="conditionOverrides[condition.key]"
                class="conditions-card__switch"
                @update:model-value="setConditionOverride(condition, $event)"
              />
            </div>
          </div>
        </div>

        <!-- Contact list card -->
        <div class="contacts-card">
          <div class="contacts-card__header">
            <p class="contacts-card__title">
              {{ t('templateBuilder.modals.sendTestV4.previewContextTitle') }}
            </p>
            <p class="contacts-card__hint">
              {{ isPhoneChannel
                ? t('templateBuilder.modals.sendTestV4.previewContextHintPhone')
                : t('templateBuilder.modals.sendTestV4.previewContextHint') }}
            </p>
          </div>
          <ContactListScroll
            :model-value="selectedContactId"
            :template-id="templateId"
            :initial-contact-info="selectedContactInfo"
            :all-contacts-only="true"
            @update:model-value="onContactSelected"
            @update:contact-info="onContactInfoUpdate"
            @update:segment-info="onSegmentInfoUpdate"
          />
        </div>
      </div>

      <!-- Right Panel: Preview render -->
      <div class="send-test-v4-right">
        <SendTestV4Preview
          :key="previewKey"
          :contact-id="selectedContactId"
          :lang="previewLang"
          :available-langs="selectedLangs"
          :condition-overrides="conditionOverrides"
          :channel="activeChannel"
          @update:lang="onPreviewLangChanged"
        />
      </div>
    </div>

    <OverlayPanel
      ref="conditionOverlayRef"
      class="send-test-condition-overlay"
    >
      <div class="send-test-condition-overlay__content">
        <SegmentationsPanel
          v-if="activeConditionPopover"
          :key="activeConditionPopover.id"
          :model-value="activeConditionPopover.config"
          size="small"
          :readonly="true"
        />
      </div>
    </OverlayPanel>

  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  Ref,
  ref,
  computed,
  ComputedRef,
  watch,
  onMounted,
  nextTick,
} from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import AutoComplete from 'primevue/autocomplete';
import InputSwitch from 'primevue/inputswitch';
import OverlayPanel from 'primevue/overlaypanel';
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import { useI18n } from 'vue-i18n';
import {
  helpers,
} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import ContactListScroll from '@/components/contacts/ContactListScroll.vue';
import SendTestV4Preview from '@/components/modals/SendTestV4Preview.vue';
import SegmentationsPanel from '@/components/segmentations/SegmentationsPanel.vue';
// eslint-disable-next-line import/no-cycle
import { TemplateEditorState } from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { useStore } from '@/store';
import {
  SaveShopsConfiguration,
  GetShopsConfigurationList,
} from '@/composables/shop/ShopsConfiguration';
import { Lang, UserState } from '@/composables/User';
import getShopsDomains from '@/composables/shop/ShopsSendDomains';
import {
  ShopsConfigurationInput,
  ShopsSendDomains,
} from '@/types/generated-types/graphql';
import { isValidEmail } from '@/helpers/Email';
import { getLocalStorageElement, setLocalStorageElement } from '@/helpers/LocalStorage';
import CountryDropdown from '@/components/fields/CountryDropdown.vue';
import RcsTestRecipientPanel from '@/components/rcs-test-send/RcsTestRecipientPanel.vue';
import { useRcsTestSend } from '@/composables/rcs/useRcsTestSend';

export interface Suggestion {
  key: string;
  value: string;
  country?: string;
}

interface SegmentCondition {
  id: number;
  config: Record<string, any>;
  key: string;
}

interface SegmentInfoPayload {
  hasSegments: boolean;
  conditions: Array<{ id: number; config: Record<string, any> }>;
  matchingCount: number;
}

export default defineComponent({
  name: 'SendTestV4Modal',

  components: {
    Dialog,
    Button,
    AutoComplete,
    InputSwitch,
    OverlayPanel,
    FieldErrors,
    ContactListScroll,
    SendTestV4Preview,
    SegmentationsPanel,
    CountryDropdown,
    RcsTestRecipientPanel,
  },

  props: {
    channel: {
      type: String,
      default: '',
    },
    rcsSenderId: {
      type: String,
      default: '',
    },
  },

  emits: {
    'on-validate': null,
  },

  setup(props, context) {
    const { t } = useI18n();
    const store = useStore();
    const displayModal: ComputedRef<boolean> = computed(() => store.state.general.sendTestEmailV4ModalVisible);
    const activeChannel = computed<'email' | 'sms' | 'whatsapp' | 'richtexting'>(() => {
      if (props.channel === 'sms') return 'sms';
      if (props.channel === 'whatsapp') return 'whatsapp';
      if (props.channel === 'richtexting') return 'richtexting';
      if (props.channel === 'email') return 'email';
      if (TemplateEditorState.template?.type === 'sms') return 'sms';
      if (TemplateEditorState.template?.type === 'whatsapp') return 'whatsapp';
      if (TemplateEditorState.template?.type === 'richtexting') return 'richtexting';
      return 'email';
    });
    const isEmailChannel = computed(() => activeChannel.value === 'email');
    const isSmsChannel = computed(() => activeChannel.value === 'sms');
    const isWhatsappChannel = computed(() => activeChannel.value === 'whatsapp');
    const isRcsChannel = computed(() => activeChannel.value === 'richtexting');
    const isPhoneChannel = computed(() => ['sms', 'whatsapp', 'richtexting'].includes(activeChannel.value));

    const rcsTestSend = useRcsTestSend();
    const rcsRecipientError = ref<string | null>(null);
    const rcsSenderOptions = computed(() => rcsTestSend.allSenderOptions.value);
    const rcsSelectedSenderId = computed(() => rcsTestSend.selectedSenderId.value);
    const rcsSelectedAgent = computed(() => rcsTestSend.selectedAgent.value);
    const rcsAvailableTestNumbers = computed(() => rcsTestSend.availableTestNumbers.value);
    const rcsSelectedTestNumbers = computed(() => rcsTestSend.selectedTestNumbers.value);
    const rcsSessionExtraNumbers = computed(() => rcsTestSend.sessionExtraNumbers.value);
    const rcsIsSharedSender = computed(() => rcsTestSend.isSharedSenderSelected.value);
    const rcsCanPersistTestNumbers = computed(() => rcsTestSend.canPersistTestNumbers.value);
    const rcsIsLoading = computed(() => rcsTestSend.isLoading.value);
    const rcsHasSelectedTestNumbers = computed(
      () => rcsTestSend.selectedTestNumbers.value.length > 0,
    );

    const initRcsTestSendState = () => {
      if (!isRcsChannel.value) return;
      rcsTestSend.prepareForModal(props.rcsSenderId || null);
    };
    const placeholderKey = computed(() => (
      isEmailChannel.value
        ? t('templateBuilder.modals.sendTestV4.sendPlaceholder')
        : t(`templateBuilder.modals.sendTest.placeholder${activeChannel.value}`)
    ));
    const channelIconClass = computed(() => {
      if (isRcsChannel.value) return 'fa-regular fa-message-smile';
      return isPhoneChannel.value ? 'fa-light fa-message' : 'fa-light fa-paper-plane';
    });

    // ========== Template ID ==========
    const templateId = computed(() =>
      TemplateEditorState.template?.id || store.state.general.sendTestEmailV4TemplateId || undefined,
    );

    const availableShopLangs = computed<Lang[]>(() => {
      const langs = UserState.activeShop?.langs ?? [];
      const sorted = [...langs];
      sorted.sort((a, b) => {
        if (a.default) return -1;
        if (b.default) return 1;
        return a.id.localeCompare(b.id);
      });
      return sorted;
    });

    const defaultShopLanguage = computed<string>(() => (
      availableShopLangs.value.find((language: Lang) => language.default)?.id
      ?? availableShopLangs.value[0]?.id
      ?? 'fr'
    ));

    const selectedLangs: Ref<string[]> = ref([]);
    const previewLang = ref('');
    const languageSelectionError = ref(false);
    const segmentConditions: Ref<SegmentCondition[]> = ref([]);
    const conditionOverrides = ref<Record<string, boolean>>({});
    const pendingStoredConditionOverrides = ref<Record<string, boolean> | null>(null);
    const conditionOverlayRef = ref();
    const activeConditionPopover = ref<SegmentCondition | null>(null);
    let conditionPopoverHideTimeout: ReturnType<typeof setTimeout> | null = null;

    const getAvailableLangIds = () => availableShopLangs.value.map((language: Lang) => language.id);
    const getConditionKey = (config: Record<string, any>) => JSON.stringify(config);

    const normalizeSelectedLangs = (langs: unknown): string[] => {
      const availableLangIds = getAvailableLangIds();
      if (!Array.isArray(langs)) return [];
      const uniqueLangs = Array.from(new Set(langs.filter((lang) => typeof lang === 'string'))) as string[];
      return uniqueLangs.filter((lang) => availableLangIds.includes(lang));
    };

    const applyLanguageSelection = (langs?: unknown, currentPreviewLang?: unknown) => {
      const availableLangIds = getAvailableLangIds();
      if (availableLangIds.length === 0) {
        selectedLangs.value = ['fr'];
        previewLang.value = 'fr';
        return;
      }

      const restoredLangs = normalizeSelectedLangs(langs);
      selectedLangs.value = restoredLangs.length > 0 ? restoredLangs : [defaultShopLanguage.value];

      if (typeof currentPreviewLang === 'string' && selectedLangs.value.includes(currentPreviewLang)) {
        previewLang.value = currentPreviewLang;
      } else {
        previewLang.value = selectedLangs.value[0];
      }
      languageSelectionError.value = false;
    };

    const toggleLanguage = (langId: string) => {
      if (selectedLangs.value.includes(langId)) {
        const remainingLangs = selectedLangs.value.filter((lang) => lang !== langId);
        if (remainingLangs.length === 0) {
          selectedLangs.value = [defaultShopLanguage.value];
          previewLang.value = defaultShopLanguage.value;
          languageSelectionError.value = false;
          return;
        }
        selectedLangs.value = remainingLangs;
      } else {
        selectedLangs.value = [...selectedLangs.value, langId];
      }

      if (!selectedLangs.value.includes(previewLang.value)) {
        previewLang.value = selectedLangs.value[0] ?? '';
      }

      if (selectedLangs.value.length > 0) {
        languageSelectionError.value = false;
      }
    };

    const onPreviewLangChanged = (langId: string) => {
      if (selectedLangs.value.includes(langId)) {
        previewLang.value = langId;
      }
    };

    const validateLanguageSelection = () => {
      if (selectedLangs.value.length === 0) {
        languageSelectionError.value = true;
        return false;
      }
      languageSelectionError.value = false;
      return true;
    };

    // ========== LocalStorage helpers (scoped per shop/user) ==========
    const getStorageKey = () => {
      const id = templateId.value;
      return id ? `sendTestV4_${id}` : null;
    };

    const saveToStorage = () => {
      const key = getStorageKey();
      if (!key) return;
      const data: Record<string, any> = {};
      if (selectedContactId.value) data.contactId = selectedContactId.value;
      if (selectedContactInfo.value) data.contactInfo = selectedContactInfo.value;
      if (isEmailChannel.value && Array.isArray(fieldValue.value) && fieldValue.value.length > 0) {
        data.emails = fieldValue.value.map((s: Suggestion) => s.value);
      }
      if (
        isPhoneChannel.value
        && !Array.isArray(fieldValue.value)
        && typeof fieldValue.value !== 'string'
        && fieldValue.value?.value
      ) {
        data.phone = fieldValue.value.value;
        data.country = countryAndState.value.country;
      }
      if (selectedLangs.value.length > 0) {
        data.langs = selectedLangs.value;
      }
      if (previewLang.value) {
        data.previewLang = previewLang.value;
      }
      if (Object.keys(conditionOverrides.value).length > 0) {
        data.conditionOverrides = conditionOverrides.value;
      }
      setLocalStorageElement(key, JSON.stringify(data));
    };

    const restoreFromStorage = () => {
      const key = getStorageKey();
      if (!key) {
        applyLanguageSelection();
        return;
      }
      try {
        const raw = getLocalStorageElement(key);
        if (!raw) {
          applyLanguageSelection();
          return;
        }
        const data = JSON.parse(raw);
        if (data.contactId) selectedContactId.value = Number(data.contactId);
        if (data.contactInfo) selectedContactInfo.value = data.contactInfo;
        if (isEmailChannel.value && Array.isArray(data.emails) && data.emails.length > 0) {
          fieldValue.value = data.emails.map((e: string) => ({ key: e, value: e }));
        }
        if (isPhoneChannel.value && data.phone) {
          fieldValue.value = { key: data.phone, value: data.phone };
          if (typeof data.country === 'string') {
            countryAndState.value.country = data.country as CountryCode;
          }
        }
        if (data.conditionOverrides && typeof data.conditionOverrides === 'object') {
          pendingStoredConditionOverrides.value = data.conditionOverrides;
        }
        applyLanguageSelection(data.langs, data.previewLang);
      } catch {
        // Ignore parse errors
        applyLanguageSelection();
      }
    };

    const synchronizeConditionOverrides = (conditions: SegmentCondition[]) => {
      const nextOverrides: Record<string, boolean> = {};
      conditions.forEach((condition) => {
        if (Object.prototype.hasOwnProperty.call(conditionOverrides.value, condition.key)) {
          nextOverrides[condition.key] = conditionOverrides.value[condition.key];
        } else if (
          pendingStoredConditionOverrides.value
          && Object.prototype.hasOwnProperty.call(pendingStoredConditionOverrides.value, condition.key)
        ) {
          nextOverrides[condition.key] = pendingStoredConditionOverrides.value[condition.key];
        } else {
          nextOverrides[condition.key] = true;
        }
      });
      conditionOverrides.value = nextOverrides;
      pendingStoredConditionOverrides.value = null;
    };

    const onSegmentInfoUpdate = (segmentInfo: SegmentInfoPayload) => {
      if (!segmentInfo?.hasSegments || !Array.isArray(segmentInfo.conditions)) {
        segmentConditions.value = [];
        conditionOverrides.value = {};
        return;
      }

      const mappedConditions: SegmentCondition[] = segmentInfo.conditions.map((condition) => ({
        id: condition.id,
        config: condition.config,
        key: getConditionKey(condition.config),
      }));
      segmentConditions.value = mappedConditions;
      synchronizeConditionOverrides(mappedConditions);
    };

    const setConditionOverride = (condition: SegmentCondition, value: boolean) => {
      conditionOverrides.value = {
        ...conditionOverrides.value,
        [condition.key]: value,
      };
    };

    const showConditionPopover = (event: Event, condition: SegmentCondition) => {
      if (conditionPopoverHideTimeout) {
        clearTimeout(conditionPopoverHideTimeout);
        conditionPopoverHideTimeout = null;
      }
      activeConditionPopover.value = condition;
      conditionOverlayRef.value?.show(event);
    };

    const hideConditionPopover = () => {
      conditionPopoverHideTimeout = setTimeout(() => {
        conditionOverlayRef.value?.hide();
        activeConditionPopover.value = null;
      }, 150);
    };

    // ========== Contact Selection ==========
    const selectedContactId: Ref<number | null> = ref(null);
    const selectedContactInfo: Ref<{ name: string; email: string } | null> = ref(null);
    const previewKey = ref(0);

    const onContactSelected = (contactId: number) => {
      selectedContactId.value = contactId;
    };

    const onContactInfoUpdate = (info: { name: string; email: string } | null) => {
      selectedContactInfo.value = info;
    };

    // ========== Email Autocomplete ==========
    const fieldValue: Ref<Suggestion | Suggestion[] | string | null> = ref(null);
    const dataSource: Ref<object[]> = ref([]);
    const domainsOfShop: Ref<ShopsSendDomains[]> = ref([]);
    const autocompleteRef = ref();
    const sendTestItems: Ref<Suggestion[]> = ref([]);

    const countryAndState = ref<{ country: CountryCode; state: string }>({
      country: (UserState.user.country ?? 'FR').toUpperCase() as CountryCode,
      state: '',
    });

    const getItems = async (): Promise<Suggestion[]> => {
      const key = isEmailChannel.value ? 'select2_list_emails_to_address' : 'select2_list_sms_to_number';
      const data = await GetShopsConfigurationList({
        shopId: UserState.activeShop?.id ?? 0,
        key,
        fields: 'id_shop_configuration, key, value',
        limit: 0,
        offset: 0,
      });

      const suggestions: Suggestion[] = [];
      if (data && data.length && data[0].value !== '') {
        const json = JSON.parse(data[0].value);

        if (json) {
          json.forEach((element: string) => {
            suggestions.push({
              key: element,
              value: element,
            });
          });
        }
      }

      return suggestions;
    };

    const search = (event: any) => {
      const current: string[] = [];

      if (isEmailChannel.value && Array.isArray(fieldValue.value)) {
        fieldValue.value.forEach((item) => {
          current.push(item.value);
        });
      } else if (isEmailChannel.value && typeof fieldValue.value === 'string' && fieldValue.value.trim()) {
        current.push(fieldValue.value.trim());
      } else if (
        isPhoneChannel.value
        && !Array.isArray(fieldValue.value)
        && typeof fieldValue.value !== 'string'
        && fieldValue.value?.value
      ) {
        current.push(fieldValue.value.value);
      } else if (isPhoneChannel.value && typeof fieldValue.value === 'string' && fieldValue.value.trim()) {
        current.push(fieldValue.value.trim().replace(new RegExp('[^0-9]', 'g'), ''));
      }

      if (!event.query.trim().length) {
        dataSource.value = [...sendTestItems.value];
      } else {
        dataSource.value = sendTestItems.value.filter(
          (item) => item.value.toLowerCase().startsWith(event.query.toLowerCase()) && !current.includes(item.value),
        );
      }

      const currentSearch: string = event.query;
      if (isEmailChannel.value) {
        const explode = currentSearch.split('@');
        const after = explode.length > 1 ? explode.pop() : '';
        const before = explode.join('@');

        domainsOfShop.value.forEach((domain: ShopsSendDomains) => {
          const returnValue = `${before}@${domain.domain}`;
          const found = dataSource.value.findIndex((el) => (el as Suggestion).value === returnValue);

          if (!current.includes(returnValue) && found === -1 && ((after && after.trim() === '') || (domain.domain.match(new RegExp(`^${after}`))))) {
            dataSource.value.unshift({
              key: returnValue,
              value: returnValue,
            });
          }
        });

        if (dataSource.value.findIndex((el) => (el as Suggestion).value === currentSearch) === -1) {
          dataSource.value.unshift({
            key: currentSearch,
            value: currentSearch,
          });
        }
      } else {
        const phoneNumber = String(currentSearch || '').trim().replace(new RegExp('[^0-9]', 'g'), '');
        if (phoneNumber) {
          dataSource.value.push({
            key: phoneNumber,
            value: phoneNumber,
          });
        }
      }
    };

    const normalizePhoneSuggestion = (item: Suggestion | string | null): Suggestion | null => {
      if (!item) return null;
      if (typeof item === 'string') {
        const normalized = item.trim().replace(new RegExp('[^0-9]', 'g'), '');
        if (!normalized) return null;
        return {
          key: normalized,
          value: normalized,
        };
      }
      return item.value ? item : null;
    };

    const validateItems = (items: Suggestion | Suggestion[] | string | null) => {
      if (isRcsChannel.value && rcsHasSelectedTestNumbers.value) {
        return true;
      }
      if (isEmailChannel.value) {
        if (typeof items === 'string') {
          return isValidEmail(items.trim());
        }
        if (Array.isArray(items) && items.length > 0) {
          return items.every((item: Suggestion) => isValidEmail(item.value));
        }
        return false;
      }
      if (!Array.isArray(items)) {
        const phoneSuggestion = normalizePhoneSuggestion(items);
        if (phoneSuggestion?.value) {
          return isValidPhoneNumber(phoneSuggestion.value, countryAndState.value.country);
        }
      }
      return false;
    };

    const getValidationMessage = () => {
      if (isEmailChannel.value) {
        return t('templateBuilder.modals.sendTest.errorEmail');
      }
      if (isRcsChannel.value) {
        return t('templateBuilder.modals.sendTest.errorRichTexting');
      }
      return t(`templateBuilder.modals.sendTest.error${activeChannel.value === 'sms' ? 'Sms' : 'Whatsapp'}`);
    };

    const getInitialFieldValue = () => (
      isEmailChannel.value ? [] : null
    );

    const mapParamsForEmit = (): Suggestion[] | Suggestion | null => {
      if (isEmailChannel.value) {
        if (typeof fieldValue.value === 'string' && fieldValue.value.trim()) {
          return [{
            key: fieldValue.value.trim(),
            value: fieldValue.value.trim(),
          }];
        }
        if (Array.isArray(fieldValue.value) && fieldValue.value.length > 0) {
          return fieldValue.value;
        }
        return [];
      }
      if (!Array.isArray(fieldValue.value)) {
        const phoneSuggestion = normalizePhoneSuggestion(fieldValue.value);
        if (!phoneSuggestion) {
          return null;
        }
        return {
          ...phoneSuggestion,
          country: countryAndState.value.country,
        };
      }
      return null;
    };

    const state = reactive({
      fieldValue,
    });
    const rules = {
      fieldValue: {
        validateItems: helpers.withMessage(
          getValidationMessage(),
          validateItems,
        ),
      },
    };
    const v$ = useVuelidate(rules, state);

    const upsertSendTestItemsConfig = async () => {
      const newConfigurationValue: string[] = [];
      sendTestItems.value.forEach((item: Suggestion) => {
        newConfigurationValue.push(item.value);
      });

      let insert = false;
      if (isEmailChannel.value && Array.isArray(fieldValue.value)) {
        fieldValue.value.forEach((input: Suggestion) => {
          if (!newConfigurationValue.includes(input.value)) {
            newConfigurationValue.push(input.value);
            insert = true;
          }
        });
      } else if (isPhoneChannel.value && !Array.isArray(fieldValue.value)) {
        const phoneSuggestion = normalizePhoneSuggestion(fieldValue.value);
        if (phoneSuggestion && !newConfigurationValue.includes(phoneSuggestion.value)) {
          newConfigurationValue.push(phoneSuggestion.value);
          insert = true;
        }
      }

      if (!insert || newConfigurationValue.length === 0) {
        return;
      }

      const key = isEmailChannel.value ? 'select2_list_emails_to_address' : 'select2_list_sms_to_number';
      const configuration: ShopsConfigurationInput = {
        id_shop: UserState.activeShop?.id ?? 0,
        configs: [
          {
            key,
            value: JSON.stringify(newConfigurationValue),
            lang: '',
          },
        ],
      };
      await SaveShopsConfiguration(configuration);
    };

    // ========== Modal Actions ==========
    const handleClose = () => {
      // Persist selections before resetting
      saveToStorage();
      fieldValue.value = getInitialFieldValue() as Suggestion[] | Suggestion | string | null;
      v$.value.$reset();
      store.commit('general/hideSendTestEmailV4Modal');
    };

    const canSendRcsTest = computed(() => {
      if (!isRcsChannel.value) return v$.value.$silentErrors.length === 0;
      if (rcsHasSelectedTestNumbers.value) return true;
      return v$.value.$silentErrors.length === 0;
    });

    const onRcsSenderChange = (senderId: string) => {
      rcsTestSend.selectedSenderId.value = senderId;
      rcsTestSend.selectedTestNumbers.value = [];
    };

    const onRcsTestNumbersChange = (numbers: string[]) => {
      rcsTestSend.selectedTestNumbers.value = numbers;
      rcsRecipientError.value = null;
    };

    const onRcsAddTestNumber = (number: string) => {
      rcsTestSend.addSessionTestNumber(number);
    };

    const onRcsPersistTestNumbers = async () => {
      await rcsTestSend.persistSessionTestNumbers();
    };

    const buildRcsParamsForEmit = (): Suggestion[] | Suggestion | null => {
      const phoneSuggestion = !Array.isArray(fieldValue.value)
        ? normalizePhoneSuggestion(fieldValue.value)
        : null;
      const recipient = phoneSuggestion
        ? { ...phoneSuggestion, country: countryAndState.value.country }
        : null;
      const built = rcsTestSend.buildRecipientParams(recipient, countryAndState.value.country);
      if (built.length === 0) {
        return null;
      }
      if (built.length === 1) {
        return built[0];
      }
      return built;
    };

    const handleValidation = async () => {
      if (!validateLanguageSelection()) {
        return;
      }

      rcsRecipientError.value = null;

      let params: Suggestion[] | Suggestion | null;
      if (isRcsChannel.value) {
        params = buildRcsParamsForEmit();
        if (!params) {
          rcsRecipientError.value = t('templateBuilder.modals.sendTestV4.rcs.recipientRequired');
          return;
        }
        if (!rcsHasSelectedTestNumbers.value) {
          const isValid = validateItems(fieldValue.value);
          if (!isValid) {
            await v$.value.$validate();
            return;
          }
        }
      } else {
        await upsertSendTestItemsConfig();
        params = mapParamsForEmit();
        if (!params) {
          return;
        }
      }

      store.commit('general/showTheSpinner');
      await new Promise<void>((resolve) => {
        const payload: Record<string, unknown> = {
          channel: activeChannel.value,
          params,
          contactId: selectedContactId.value,
          langs: selectedLangs.value,
          conditionOverrides: conditionOverrides.value,
          done: resolve,
        };
        if (isRcsChannel.value && rcsTestSend.selectedSenderId.value) {
          payload.senderId = rcsTestSend.selectedSenderId.value;
          payload.rcsSenderId = rcsTestSend.selectedSenderId.value;
        }
        context.emit('on-validate', payload);
      });
      store.commit('general/hideTheSpinner');

      saveToStorage();
    };

    onMounted(() => {
      if (displayModal.value) {
        getItems().then((data) => {
          sendTestItems.value = data;
        });

        getShopsDomains(0).then((data) => {
          domainsOfShop.value = data;
        });

        initRcsTestSendState();
        restoreFromStorage();
      }
    });

    watch(displayModal, (newVal) => {
      if (newVal) {
        getItems().then((data) => {
          sendTestItems.value = data;
        });

        getShopsDomains(0).then((data) => {
          domainsOfShop.value = data;
        });

        initRcsTestSendState();

        restoreFromStorage();
        previewKey.value++;

        nextTick().then(() => {
          if (autocompleteRef.value) {
            const input = autocompleteRef.value.$el.querySelector('.send-card__autocomplete-input');
            if (input) {
              setTimeout(() => {
                input.focus();
              }, 0);
            }
          }
        });
      }
    });

    watch(activeChannel, async (newChannel, oldChannel) => {
      if (newChannel === oldChannel) return;
      fieldValue.value = getInitialFieldValue() as Suggestion[] | Suggestion | string | null;
      v$.value.$reset();
      sendTestItems.value = await getItems();
      if (isEmailChannel.value) {
        domainsOfShop.value = await getShopsDomains(0);
      } else {
        domainsOfShop.value = [];
      }
      restoreFromStorage();
      previewKey.value += 1;
    });

    return {
      t,
      v$,
      displayModal,
      // Template & Contact selection
      templateId,
      selectedContactId,
      selectedContactInfo,
      previewKey,
      previewLang,
      selectedLangs,
      activeChannel,
      isEmailChannel,
      isSmsChannel,
      isWhatsappChannel,
      isRcsChannel,
      isPhoneChannel,
      rcsSenderOptions,
      rcsSelectedSenderId,
      rcsSelectedAgent,
      rcsAvailableTestNumbers,
      rcsSelectedTestNumbers,
      rcsSessionExtraNumbers,
      rcsIsSharedSender,
      rcsCanPersistTestNumbers,
      rcsIsLoading,
      rcsRecipientError,
      rcsHasSelectedTestNumbers,
      canSendRcsTest,
      onRcsSenderChange,
      onRcsTestNumbersChange,
      onRcsAddTestNumber,
      onRcsPersistTestNumbers,
      placeholderKey,
      channelIconClass,
      availableShopLangs,
      segmentConditions,
      conditionOverrides,
      languageSelectionError,
      onContactSelected,
      onContactInfoUpdate,
      onPreviewLangChanged,
      toggleLanguage,
      onSegmentInfoUpdate,
      setConditionOverride,
      conditionOverlayRef,
      activeConditionPopover,
      showConditionPopover,
      hideConditionPopover,
      // Email input
      dataSource,
      search,
      autocompleteRef,
      countryAndState,
      // Actions
      handleClose,
      handleValidation,
    };
  },
});
</script>

<style scoped lang="scss">
.send-test-v4-layout {
  display: flex;
  height: calc(100vh - 110px);
  gap: 1rem;
  padding: 1rem;
  overflow: hidden;
  background: #f8fafc;
}

// ---- Left panel ----
.send-test-v4-left {
  flex: 0 0 38%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  overflow: hidden;
}

.conditions-card {
  flex-shrink: 0;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  padding: 0.7rem 0.85rem;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.45rem;
  }

  &__title {
    font-size: 0.75rem;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  &__hint {
    margin: 0 0 0.5rem;
    font-size: 0.75rem;
    color: #64748b;
    line-height: 1.35;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.5rem 0.6rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
  }

  &__badge {
    font-size: 0.68rem;
    font-weight: 700;
    color: #5f8f1a;
    background: #ecf8d8;
    border-radius: 999px;
    padding: 0.08rem 0.45rem;
    white-space: nowrap;
  }

  &__label {
    font-size: 0.79rem;
    font-weight: 600;
    color: #334155;
  }

  &__info {
    margin-left: auto;
    color: #94a3b8;
    cursor: pointer;
    font-size: 0.85rem;

    &:hover {
      color: #64748b;
    }
  }

  &__switch {
    margin-left: 0.2rem;
  }
}

// ---- Send card ----
.send-card {
  flex-shrink: 0;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &__inner {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.625rem 0.625rem 0.625rem 1rem;

    @media (max-width: 1280px) {
      flex-wrap: wrap;
      row-gap: 0.5rem;
    }
  }

  &__sms {
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
    padding: 0.75rem 0.85rem 0.85rem;
    background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  }

  &__sms--rcs {
    .send-card__rcs-recipient-title {
      margin: 0.15rem 0 0;
    }

    .send-card__sms-row--recipient {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 0.35rem;
      grid-template-columns: none;
    }
  }

  &__sms-row {
    display: grid;
    grid-template-columns: 40px minmax(0, 1fr);
    gap: 0.6rem;
    align-items: center;
    width: 100%;
    min-width: 0;
  }

  &__sms-footer {
    display: flex;
    align-items: flex-end;
    justify-content: flex-start;
    gap: 0.75rem;
    min-height: 2.55rem;
  }

  &__languages {
    border-top: 1px solid #e2e8f0;
    padding: 0.65rem 0.9rem 0.8rem;
  }

  &__languages-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;
  }

  &__languages-title {
    font-size: 0.75rem;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  &__languages-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
  }

  &__language-item {
    border: 1px solid #dbe3ee;
    border-radius: 999px;
    background: #fff;
    color: #334155;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.28rem 0.6rem;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: #94c748;
      background: #f8fced;
    }

    &--active {
      border-color: #94c748;
      background: #f0f8dc;
      color: #365314;
    }
  }

  &__language-flag {
    width: 16px;
    height: 12px;
    border-radius: 2px;
    object-fit: cover;
    display: block;
  }

  &__language-code {
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1;
  }

  &__language-check {
    font-size: 0.675rem;
  }

  &__languages-error {
    margin: 0.45rem 0 0;
    font-size: 0.75rem;
    color: #dc2626;
  }

  &__icon {
    color: #94a3b8;
    font-size: 1rem;
    flex-shrink: 0;
  }

  &__icon--sms {
    width: 40px;
    height: 40px;
    border: 1px solid #dbe6f3;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #8ca0bf;
    background: #fff;
  }

  &__field {
    min-width: 0;
  }

  &__sms-input-group {
    display: grid;
    grid-template-columns: 150px minmax(0, 1fr);
    gap: 0;
    min-width: 0;
    width: 100%;
    overflow: hidden;
    border-radius: 6px;
    align-items: stretch;
    border: 1px solid #d4dfec;
    background: #fff;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

    &:focus-within {
      border-color: #94c748;
      box-shadow: 0 0 0 3px rgba(148, 199, 72, 0.15);
    }

    @media (max-width: 1400px) {
      grid-template-columns: 136px minmax(0, 1fr);
    }
  }

  &__field--country {
    display: flex;
    align-items: stretch;

    :deep(.p-dropdown) {
      border: none;
      border-radius: 0;
      border-right-width: 1px;
      min-height: 38px;
      height: 38px;
      box-shadow: none;
      transition: all 0.2s ease;

      &:hover {
        border: none;
      }

      &:focus-within {
        border: none;
        box-shadow: none;
      }
    }

    :deep(.p-dropdown .p-dropdown-label) {
      padding-top: 0.45rem;
      padding-bottom: 0.45rem;
      font-size: 0.92rem;
      padding-left: 0.65rem;
      padding-right: 0.4rem;
    }
  }

  &__field--phone {
    min-width: 0;
    display: flex;
    align-items: stretch;
    border-left: 1px solid #d4dfec;

    :deep(.p-autocomplete) {
      width: 100%;
      display: block;
    }

    :deep(.p-autocomplete-input) {
      width: 100%;
      min-height: 38px;
      height: 38px;
      border-radius: 0;
      border: none;
      box-shadow: none;
      transition: all 0.2s ease;
      font-size: 0.95rem;

      &:hover {
        border: none;
      }

      &:focus {
        border: none;
        box-shadow: none;
      }
    }
  }

  &__country {
    width: 100%;
    min-width: 0;
    flex-shrink: 0;

    :deep(.p-dropdown) {
      width: 100%;
    }
  }

  &__input {
    flex: 1;
    min-width: 0;

    :deep(.p-autocomplete) {
      width: 100%;
    }

    :deep(.p-autocomplete-multiple-container) {
      border: none !important;
      padding: 0.25rem 0 !important;
      box-shadow: none !important;
      background: transparent !important;
      min-height: 2.25rem;
      flex-wrap: wrap;
      width: 100%;
    }

    :deep(.p-autocomplete-input-token) {
      padding: 0 !important;
      flex: 1;
    }

    :deep(.p-autocomplete-input-token input) {
      font-size: 0.875rem !important;
      color: #334155;
      padding: 0.375rem 0 !important;
      width: 100% !important;
    }

    :deep(.p-autocomplete-input-token input::placeholder) {
      color: #94a3b8;
      font-size: 0.8125rem;
    }

    :deep(.p-autocomplete-token) {
      background: #f0fdf4;
      color: #166534;
      border-radius: 4px;
      font-size: 0.8125rem;
      font-weight: 500;
      max-width: 100%;
    }

    :deep(.p-autocomplete-token-label) {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__errors {
    margin-top: 0.25rem;

    :deep(.input-errors) {
      margin: 0;
    }
  }

  &__errors--sms {
    order: 1;
    margin-top: 0;
    color: #ef4444;
    font-weight: 600;
    font-size: 0.83rem;
    line-height: 1.2;
    min-height: 2.1rem;
    display: flex;
    align-items: center;

    :deep(.input-errors) {
      display: flex;
      align-items: center;
      gap: 0.35rem;
    }

    :deep(.input-errors::before) {
      content: "!";
      width: 1.15rem;
      height: 1.15rem;
      border-radius: 999px;
      border: 2px solid currentColor;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 0.72rem;
      font-weight: 700;
      flex-shrink: 0;
    }
  }

  &__btn {
    flex-shrink: 0;
    border-radius: 6px !important;
    font-weight: 600 !important;
    font-size: 0.8125rem !important;
    padding: 0.5rem 1.25rem !important;
    white-space: nowrap;

    @media (max-width: 1280px) {
      margin-left: auto;
    }
  }

  &__btn--sms {
    order: 2;
    margin-left: auto;
    min-width: 180px;
    min-height: 38px;
    border-radius: 6px !important;
    box-shadow: 0 6px 18px rgba(148, 199, 72, 0.28);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding-inline: 1.1rem !important;
  }
}

.send-card__recipient-heading {
  flex: 1;
  min-width: 0;

  &--inline {
    margin-bottom: 0.25rem;
  }
}

.send-card__recipient-label {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.send-card__recipient-hint {
  margin: 0.2rem 0 0;
  font-size: 0.78rem;
  line-height: 1.35;
  color: #94a3b8;
}

// ---- Contact list card ----
.contacts-card {
  flex: 0 1 auto;
  min-height: 0;
  max-height: 550px;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;

  &__header {
    flex-shrink: 0;
    padding: 0.75rem 1rem 0.65rem;
    border-bottom: 1px solid #f1f5f9;
    background: #f8fafc;
  }

  &__title {
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 700;
    color: #1e293b;
  }

  &__hint {
    margin: 0.3rem 0 0;
    font-size: 0.75rem;
    line-height: 1.4;
    color: #64748b;
  }

  :deep(.contact-list-scroll) {
    flex: 1;
    min-height: 0;
  }
}

// ---- Right panel ----
.send-test-v4-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
</style>

<style lang="scss">
.send-test-condition-overlay {
  border-radius: 12px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  overflow: hidden;

  .p-overlaypanel-content {
    padding: 0;
  }

  &__content {
    min-width: 320px;
    max-width: 520px;
    min-height: 60px;
    padding: 1rem;
    background: #f8fafc;
  }
}
</style>
